if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (this.hasAttribute('data-etsy-gallery')) return;
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }


      connectedCallback() {
        if (!this.hasAttribute('data-etsy-gallery')) return;
        this.galleryAbort?.abort();
        this.galleryAbort = new AbortController();
        const options = { signal: this.galleryAbort.signal };
        this.galleryList = this.elements.viewer.querySelector('ul');
        this.addEventListener('click', (event) => {
          const direction = event.target.closest('[data-gallery-direction]');
          const thumb = event.target.closest('[data-target] button');
          if (direction) this.stepGallery(Number(direction.dataset.galleryDirection));
          if (thumb) this.activateGalleryMedia(thumb.closest('[data-target]').dataset.target);
        }, options);
        this.addEventListener('keydown', (event) => {
          if (!event.target.closest('button') || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
          event.preventDefault();
          this.stepGallery(event.key === 'ArrowRight' ? 1 : -1);
        }, options);
        this.galleryList.addEventListener('scroll', () => {
          if (this.mql.matches) return;
          cancelAnimationFrame(this.galleryScrollFrame);
          this.galleryScrollFrame = requestAnimationFrame(() => {
            const slides = this.gallerySlides();
            const left = this.galleryList.getBoundingClientRect().left;
            const nearest = slides.reduce((best, slide) => Math.abs(slide.getBoundingClientRect().left - left) < Math.abs(best.getBoundingClientRect().left - left) ? slide : best, slides[0]);
            if (nearest && !nearest.classList.contains('is-active')) this.activateGalleryMedia(nearest.dataset.mediaId, false);
          });
        }, options);
        this.mql.addEventListener('change', () => {
          const active = this.galleryList.querySelector('.is-active') || this.gallerySlides()[0];
          if (active) this.activateGalleryMedia(active.dataset.mediaId);
        }, options);
        document.addEventListener('personalised-name-change', (event) => {
          const preview = this.querySelector('[data-preview-display]');
          const formId = event.detail?.formId || event.detail?.productFormId;
          if (!preview || (formId && formId !== preview.dataset.productFormId)) return;
          this.activateGalleryMedia(this.querySelector('[data-live-preview-slide]').dataset.mediaId);
          this.syncPreviewThumbnail();
        }, options);
        this.galleryObserver = new MutationObserver(() => this.syncPreviewThumbnail());
        this.galleryObserver.observe(this.galleryList, { childList: true, subtree: true, attributes: true, attributeFilter: ['src'] });
        const initial = this.galleryList.querySelector('.is-active') || this.gallerySlides()[0];
        if (initial) this.activateGalleryMedia(initial.dataset.mediaId, false);
      }

      disconnectedCallback() {
        this.galleryAbort?.abort();
        this.galleryObserver?.disconnect();
        cancelAnimationFrame(this.galleryScrollFrame);
      }

      gallerySlides() {
        return Array.from(this.galleryList.querySelectorAll(':scope > [data-media-id]'));
      }

      syncPreviewThumbnail() {
        const source = this.querySelector('[data-preview-background] img');
        const target = this.querySelector('[data-target$="-preview"] img');
        if (source && target && target.src !== source.src) {
          target.removeAttribute('srcset');
          target.src = source.src;
        }
      }

      stepGallery(direction) {
        const slides = this.gallerySlides();
        if (slides.length < 2) return;
        const index = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
        this.activateGalleryMedia(slides[(index + direction + slides.length) % slides.length].dataset.mediaId);
      }

      activateGalleryMedia(mediaId, scroll = true) {
        const slides = this.gallerySlides();
        const active = slides.find((slide) => slide.dataset.mediaId === mediaId) || slides[0];
        if (!active) return;
        const changed = !active.classList.contains('is-active');
        slides.forEach((slide) => {
          slide.classList.toggle('is-active', slide === active);
          slide.inert = slide !== active;
        });
        this.elements.thumbnails?.querySelectorAll('[data-target]').forEach((thumbnail) => {
          const selected = thumbnail.dataset.target === active.dataset.mediaId;
          const button = thumbnail.querySelector('button');
          if (selected) button.setAttribute('aria-current', 'true');
          else button.removeAttribute('aria-current');
          if (selected) {
            const list = thumbnail.parentElement;
            const itemRect = thumbnail.getBoundingClientRect();
            const listRect = list.getBoundingClientRect();
            if (this.mql.matches) {
              if (itemRect.top < listRect.top) list.scrollTop += itemRect.top - listRect.top;
              else if (itemRect.bottom > listRect.bottom) list.scrollTop += itemRect.bottom - listRect.bottom;
            } else {
              if (itemRect.left < listRect.left) list.scrollLeft += itemRect.left - listRect.left;
              else if (itemRect.right > listRect.right) list.scrollLeft += itemRect.right - listRect.right;
            }
          }
        });
        if (scroll && !this.mql.matches) this.galleryList.scrollTo({ left: active.offsetLeft - slides[0].offsetLeft, behavior: 'instant' });
        if (changed) {
          this.playActiveMedia(active);
          this.elements.liveRegion.textContent = 'Media ' + (slides.indexOf(active) + 1) + ' of ' + slides.length;
        }
        this.syncPreviewThumbnail();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        if (this.hasAttribute('data-etsy-gallery')) {
          const preview = this.querySelector('[data-live-preview-slide]');
          this.activateGalleryMedia(prepend && preview ? preview.dataset.mediaId : mediaId);
          return;
        }
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
